import React from 'react';
import DsButton from '../../common/DsButton';
import DsModal from '../../common/DsModal';
import TableSkeletonRows from '../../common/TableSkeletonRows';
import { SkeletonAvatar, SkeletonLine } from '../../common/Skeleton';

const cancelCompTargetSkeletonColumns = [
  { key: 'target', variant: 'circle', size: 18, align: 'center' },
  { key: 'artist', variant: 'profile', size: 28, primaryWidth: '64%', secondaryWidth: '38%' },
  { key: 'fee', variant: 'line', width: '54%' },
  { key: 'current', variant: 'line', width: '62%' },
];

const AssignmentCandidateSkeleton = () => (
  <>
    {[0, 1, 2, 3, 4].map((index) => (
      <div key={`assignment-candidate-skeleton-${index}`} className="event-assignment-settings-modal__row" aria-hidden="true">
        <SkeletonAvatar size={24} />
        <SkeletonLine width={index === 2 ? '48%' : '68%'} height={12} />
      </div>
    ))}
  </>
);

export default function EventDetailsAdminModals({ view }) {
  const {
    assignmentCandidatesLoading,
    assignmentDraftIds,
    assignmentSaving,
    assignmentSearch,
    assignmentSettingsOpen,
    canCancelEvent,
    cancelCompCalcBusy,
    cancelCompFixed,
    cancelCompLoading,
    cancelCompMode,
    cancelCompPercent,
    cancelCompSaving,
    cancelCompTargets,
    cancelReason,
    closeCancelCompModal,
    closeCancelModal,
    currentFlyerUrl,
    eventData,
    filteredAssignmentCandidates,
    flyerToggleLabel,
    handleApplyCancelCompBulk,
    handleAssignmentSettingsSave,
    handleFlyerPopupInnerClick,
    hasMultipleFlyers,
    loggedInAdminId,
    openCancelModal,
    plannedCancelCompByContractArtistId,
    proceedCancelPreview,
    setAssignmentSearch,
    setAssignmentSettingsOpen,
    setCancelCompFixed,
    setCancelCompMode,
    setCancelCompPercent,
    setCancelCompTargets,
    setCancelReason,
    setShowFlyerPopup,
    showCancelCompModal,
    showCancelConfirm,
    showCancelModal,
    showFlyerPopup,
    submitCancel,
    submittingCancel,
    toggleAssignmentDraftId,
    toggleFlyerFace,
  } = view;

  return (
    <>
          {/* 中止ボタン */}
          {canCancelEvent && !eventData?.canceled_at && (
            <div className="event-cancel-action">
              <DsButton variant="danger" onClick={openCancelModal}>
                イベントを中止する
              </DsButton>
            </div>
          )}

          {assignmentSettingsOpen ? (
            <DsModal
              open={assignmentSettingsOpen}
              onClose={() => setAssignmentSettingsOpen(false)}
              title="担当者設定"
              size="md"
              dismissible={!assignmentSaving}
              className="event-assignment-settings-modal"
            >
                <div className="event-assignment-settings-modal__quick">
                  <DsButton
                    type="button"
                    variant="secondary"
                    onClick={() => toggleAssignmentDraftId(loggedInAdminId)}
                    disabled={!loggedInAdminId || assignmentSaving}
                  >
                    {assignmentDraftIds.some((id) => Number(id) === Number(loggedInAdminId))
                      ? '自分を担当から外す'
                      : '自分を担当に追加'}
                  </DsButton>
                  <span>{assignmentDraftIds.length}人を選択中</span>
                </div>
                <input
                  className="event-assignment-settings-modal__search"
                  type="search"
                  value={assignmentSearch}
                  onChange={(event) => setAssignmentSearch(event.target.value)}
                  placeholder="管理者名で検索"
                />
                <div className="event-assignment-settings-modal__list">
                  {assignmentCandidatesLoading ? (
                    <AssignmentCandidateSkeleton />
                  ) : filteredAssignmentCandidates.length ? (
                    filteredAssignmentCandidates.map((admin) => {
                      const id = Number(admin?.id ?? admin?.admin_user_id);
                      const checked = assignmentDraftIds.some((value) => Number(value) === id);
                      const label = admin?.display_name || admin?.full_name || admin?.nickname || admin?.username || `ID ${id}`;
                      return (
                        <label key={id} className="event-assignment-settings-modal__row">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleAssignmentDraftId(id)}
                            disabled={assignmentSaving}
                          />
                          <span>
                            <strong>{label}</strong>
                            {[admin?.department, admin?.position].filter(Boolean).length ? (
                              <em>{[admin.department, admin.position].filter(Boolean).join(' / ')}</em>
                            ) : null}
                          </span>
                        </label>
                      );
                    })
                  ) : (
                    <p className="event-assignment-settings-modal__empty">担当者候補が見つかりません。</p>
                  )}
                </div>
                <div className="modal-actions">
                  <DsButton
                    block
                    onClick={handleAssignmentSettingsSave}
                    disabled={assignmentSaving}
                    loading={assignmentSaving}
                  >
                    {assignmentSaving ? '保存中...' : '保存'}
                  </DsButton>
                </div>
            </DsModal>
          ) : null}

          {/* 中止理由入力モーダル */}
          {showCancelModal ? (
            <DsModal
              open={showCancelModal}
              onClose={closeCancelModal}
              title="イベント中止理由の入力"
              size="sm"
              className="event-cancel-reason-modal"
            >
                <p className="muted">中止理由を入力してください。</p>
                <p className="warning-text">参加者全員に通知がいきます。</p>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  rows={4}
                  style={{ width: '100%' }}
                  placeholder="例：主催側の都合により中止"
                />
                <div className="modal-actions">
                  <DsButton block onClick={proceedCancelPreview}>
                    中止内容を確認
                  </DsButton>
                </div>
            </DsModal>
          ) : null}

          {/* 中止確認モーダル */}
          {showCancelConfirm ? (
            <DsModal
              open={showCancelConfirm}
              onClose={closeCancelModal}
              title="イベント中止の最終確認"
              size="sm"
              className="event-cancel-confirm-modal"
            >
                <p className="warning-text">一度中止を決定すると元には戻せません。</p>
                <p className="warning-text">参加者全員に通知がいきます。</p>
                <div className="muted" style={{ marginBottom: 8 }}>
                  理由: {cancelReason ? cancelReason : '（未入力）'}
                </div>
                <div className="modal-actions">
                  <DsButton variant="secondary" onClick={closeCancelModal}>
                    戻る
                  </DsButton>
                  <DsButton
                    variant="danger"
                    onClick={submitCancel}
                    disabled={submittingCancel}
                    loading={submittingCancel}
                  >
                    {submittingCancel ? '処理中...' : '中止を実行する'}
                  </DsButton>
                </div>
            </DsModal>
          ) : null}

          {/* 中止保証一括設定モーダル */}
          {showCancelCompModal ? (
            <DsModal
              open={showCancelCompModal}
              onClose={closeCancelCompModal}
              title="中止保証金額の一括設定"
              size="lg"
              dismissible={!cancelCompSaving && !cancelCompCalcBusy}
              className="cancel-comp-bulk-modal"
            >
                {cancelCompLoading || cancelCompCalcBusy ? (
                  <div className="cancel-comp-bulk-modal__calc-overlay" role="status" aria-live="polite">
                    <div className="cancel-comp-bulk-modal__calc-card">
                      <div className="cancel-comp-bulk-modal__calc-spinner" aria-hidden="true" />
                      <div className="cancel-comp-bulk-modal__calc-text">計算中...</div>
                    </div>
                  </div>
                ) : null}

                <p className="cancel-comp-bulk-modal__desc">
                  中止となったイベントの出演者に対する保証金額をまとめて設定します。保証がない場合は 0 円、または 0％ を設定してください。
                </p>

                <div className="cancel-comp-bulk-modal__note" role="note">
                  <i className="fa-solid fa-circle-info" aria-hidden="true" />
                  <span>保証金額が決定済みのアーティストは選択できません。</span>
                </div>

                <div className="cancel-comp-bulk-modal__fields">
                  <div className="cancel-comp-bulk-modal__field cancel-comp-bulk-modal__field--mode">
                    <div className="cancel-comp-bulk-modal__label">入力方法</div>
                    <div className="cancel-comp-bulk-modal__radio-group" role="radiogroup">
                      <label
                        className={`cancel-comp-bulk-modal__radio${
                          cancelCompMode === 'percent' ? ' is-selected' : ''
                        }`}
                      >
                        <input
                          type="radio"
                          name="cancel-comp-bulk-mode"
                          value="percent"
                          checked={cancelCompMode === 'percent'}
                          onChange={() => setCancelCompMode('percent')}
                          disabled={cancelCompSaving || cancelCompCalcBusy}
                          className="cancel-comp-bulk-modal__radio-input"
                        />
                        <span>依頼金額に対する％</span>
                      </label>
                      <label
                        className={`cancel-comp-bulk-modal__radio${
                          cancelCompMode === 'fixed' ? ' is-selected' : ''
                        }`}
                      >
                        <input
                          type="radio"
                          name="cancel-comp-bulk-mode"
                          value="fixed"
                          checked={cancelCompMode === 'fixed'}
                          onChange={() => setCancelCompMode('fixed')}
                          disabled={cancelCompSaving || cancelCompCalcBusy}
                          className="cancel-comp-bulk-modal__radio-input"
                        />
                        <span>固定金額（全員同額）</span>
                      </label>
                    </div>
                  </div>

                  <div className="cancel-comp-bulk-modal__field">
                    <div className="cancel-comp-bulk-modal__label">
                      {cancelCompMode === 'percent' ? '補償割合（％）' : '補償金額（円）'}
                    </div>
                    <div className="cancel-comp-bulk-modal__input-row">
                      {cancelCompMode === 'percent' ? (
                        <input
                          type="number"
                          min="0"
                          className="cancel-comp-bulk-modal__input cancel-comp-bulk-modal__input--percent"
                          value={cancelCompPercent}
                          onChange={(e) => setCancelCompPercent(e.target.value)}
                          placeholder="例: 80"
                          disabled={cancelCompSaving || cancelCompCalcBusy}
                        />
                      ) : (
                        <input
                          type="number"
                          min="0"
                          className="cancel-comp-bulk-modal__input cancel-comp-bulk-modal__input--amount"
                          value={cancelCompFixed}
                          onChange={(e) => setCancelCompFixed(e.target.value)}
                          placeholder="例: 30000"
                          disabled={cancelCompSaving || cancelCompCalcBusy}
                        />
                      )}
                      <span className="cancel-comp-bulk-modal__unit">
                        {cancelCompMode === 'percent' ? '%' : '円'}
                      </span>
                    </div>
                  </div>

                </div>

                <div className="table-responsive cancel-comp-bulk-modal__table-wrap">
                  <table className="cancel-comp-bulk-table">
                    <thead>
                      <tr>
                        <th>対象</th>
                        <th>アーティスト名</th>
                        <th>依頼金額</th>
                        <th>現在の中止保証</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cancelCompLoading ? (
                        <TableSkeletonRows rows={5} columns={cancelCompTargetSkeletonColumns} />
                      ) : cancelCompTargets.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="cancel-comp-bulk-table__empty">
                            対象となるアーティストがいません。
                          </td>
                        </tr>
                      ) : (
                        cancelCompTargets.map((t) => (
                          <tr
                            key={t.contract_artist_id}
                            className={[
                              t.selection_locked ? 'is-locked' : '',
                              !t.selection_locked && !t.excluded ? 'is-selected' : '',
                            ]
                              .filter(Boolean)
                              .join(' ')}
                            onClick={() => {
                              if (cancelCompSaving || cancelCompCalcBusy) return;
                              if (t.selection_locked) return;
                              setCancelCompTargets((prev) =>
                                prev.map((row) =>
                                  row.contract_artist_id === t.contract_artist_id
                                    ? { ...row, excluded: !row.excluded }
                                    : row
                                )
                              );
                            }}
                            role={t.selection_locked ? undefined : 'button'}
                            tabIndex={t.selection_locked ? -1 : 0}
                            aria-disabled={t.selection_locked ? 'true' : undefined}
                            onKeyDown={(e) => {
                              if (cancelCompSaving || cancelCompCalcBusy) return;
                              if (t.selection_locked) return;
                              if (e.key !== 'Enter' && e.key !== ' ') return;
                              e.preventDefault();
                              setCancelCompTargets((prev) =>
                                prev.map((row) =>
                                  row.contract_artist_id === t.contract_artist_id
                                    ? { ...row, excluded: !row.excluded }
                                    : row
                                )
                              );
                            }}
                          >
                            <td className="cancel-comp-bulk-table__check">
                              <input
                                type="checkbox"
                                checked={!t.excluded && !t.selection_locked}
                                disabled={!!t.selection_locked}
                                onClick={(e) => e.stopPropagation()}
                                onChange={() => {
                                  if (cancelCompSaving || cancelCompCalcBusy) return;
                                  if (t.selection_locked) return;
                                  setCancelCompTargets((prev) =>
                                    prev.map((row) =>
                                      row.contract_artist_id === t.contract_artist_id
                                        ? { ...row, excluded: !row.excluded }
                                        : row
                                    )
                                  );
                                }}
                              />
                            </td>
                            <td>{t.artist_name}</td>
                            <td>{Number(t.fee || 0).toLocaleString()} 円</td>
                            <td>
                              <div className="cancel-comp-bulk-table__cancel-cell">
                                <div className="cancel-comp-bulk-table__cancel-current">
                                  {t.event_cancel_amount != null
                                    ? `${Number(t.event_cancel_amount || 0).toLocaleString()} 円`
                                    : ''}
                                  {t.selection_locked ? (
                                    <span className="cancel-comp-locked-badge">決定済み</span>
                                  ) : null}
                                </div>
                                {!t.selection_locked && !t.excluded ? (
                                  (() => {
                                    const planned = plannedCancelCompByContractArtistId.get(
                                      t.contract_artist_id
                                    );
                                    if (planned == null) return null;
                                    return (
                                      <div className="cancel-comp-bulk-table__cancel-planned">
                                        → {Number(planned || 0).toLocaleString()} 円
                                      </div>
                                    );
                                  })()
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="modal-actions cancel-comp-bulk-modal__actions">
                  <DsButton
                    block
                    onClick={handleApplyCancelCompBulk}
                    disabled={cancelCompSaving || cancelCompLoading || cancelCompCalcBusy}
                    loading={cancelCompSaving}
                  >
                    {cancelCompSaving ? '設定中...' : '中止保証金額を決定'}
                  </DsButton>
                </div>
            </DsModal>
          ) : null}

          {showFlyerPopup && (
            <div className="flyer-popup" onClick={() => setShowFlyerPopup(false)}>
              <div className="flyer-popup-inner" onClick={handleFlyerPopupInnerClick}>
                {currentFlyerUrl && (
                  <img src={currentFlyerUrl} alt="拡大フライヤー" className="expanded-flyer" />
                )}
                {hasMultipleFlyers && (
                  <button
                    type="button"
                    className="flyer-popup-toggle"
                    onClick={toggleFlyerFace}
                  >
                    {flyerToggleLabel}
                  </button>
                )}
              </div>
            </div>
          )}
    </>
  );
}
